import React, { useState, useEffect } from 'react';
import { Settings, Clock, RefreshCw } from 'lucide-react';

const Maintenance = ({ message, expectedBack, showCountdown }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!expectedBack || !showCountdown) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(expectedBack);
      const diff = target - now;

      if (diff <= 0) {
        return null;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      if (!newTimeLeft) {
        clearInterval(timer);
        // Auto refresh after countdown ends
        window.location.reload();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expectedBack, showCountdown]);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        {/* Animated Logo/Icon */}
        <div className="mb-8 relative">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-emerald-500/20 animate-pulse">
            <Settings className="w-12 h-12 text-white animate-spin-slow" style={{ animationDuration: '8s' }} />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center animate-bounce" style={{ left: 'calc(50% + 32px)' }}>
            <span className="text-white text-xs font-bold">!</span>
          </div>
        </div>

        {/* Main Heading */}
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          We'll Be Back Soon
        </h1>

        {/* Subheading */}
        <p className="text-xl text-slate-300 mb-8">
          Discover Essex is currently undergoing maintenance
        </p>

        {/* Message Box */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 mb-8">
          <p className="text-slate-200 leading-relaxed">
            {message || "We're currently performing scheduled maintenance to improve your experience. We'll be back shortly!"}
          </p>
        </div>

        {/* Countdown Timer */}
        {showCountdown && timeLeft && (
          <div className="mb-8">
            <p className="text-sm text-slate-400 mb-3 flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              Estimated time remaining
            </p>
            <div className="flex justify-center gap-4">
              <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 min-w-[70px]">
                <div className="text-2xl font-bold text-emerald-400">{String(timeLeft.hours).padStart(2, '0')}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Hours</div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 min-w-[70px]">
                <div className="text-2xl font-bold text-emerald-400">{String(timeLeft.minutes).padStart(2, '0')}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Minutes</div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 min-w-[70px]">
                <div className="text-2xl font-bold text-emerald-400">{String(timeLeft.seconds).padStart(2, '0')}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Seconds</div>
              </div>
            </div>
          </div>
        )}

        {/* Expected Back Time (if no countdown) */}
        {expectedBack && !showCountdown && (
          <div className="mb-8 text-slate-400">
            <Clock className="w-4 h-4 inline-block mr-2" />
            Expected back: {new Date(expectedBack).toLocaleString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-medium transition-all hover:scale-105 active:scale-95"
        >
          <RefreshCw className="w-4 h-4" />
          Check Again
        </button>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <p className="text-slate-500 text-sm">
            Thank you for your patience
          </p>
          <p className="text-slate-600 text-xs mt-2">
            If you need urgent assistance, please email{' '}
            <a href="mailto:contact@discoveressex.co.uk" className="text-emerald-500 hover:text-emerald-400">
              contact@discoveressex.co.uk
            </a>
          </p>
          <p className="text-slate-700 text-xs mt-4">
            <a href="/login" className="text-slate-500 hover:text-slate-400 underline">
              Login
            </a>
          </p>
        </div>
      </div>

      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
};

export default Maintenance;
